import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  Upload,
  Image as ImageIcon,
  Video,
  RefreshCw,
  Settings,
  Search,
  CheckCircle2,
  Clock,
  MessageSquare,
  Lock,
  Trash2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const TierUpload = ({ creator, tier, getAuthHeaders, onUploaded }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [dragOver, setDragOver] = useState(false);
  const tierData = creator[tier];

  if (!tierData.configured) {
    return (
      <div className="bg-white/5 border border-dashed border-white/10 rounded-lg p-3 text-center">
        <p className="text-white/40 text-[11px]">Canal {tier.toUpperCase()} sin configurar</p>
        <Link
          to={`/admin/creadoras/${creator.username}/auto-posting`}
          className="text-[11px] text-blue-300 hover:text-blue-200"
        >
          Configurar →
        </Link>
      </div>
    );
  }

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let okCount = 0;
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("file", files[i]);
      fd.append("tier", tier);
      try {
        const r = await fetch(
          `/api/admin/autopost/${creator.username}/media/upload`,
          {
            method: "POST",
            headers: { ...getAuthHeaders() },
            body: fd,
          }
        );
        if (r.ok) okCount += 1;
      } catch (e) {
        // continue
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setUploading(false);
    setProgress({ done: 0, total: 0 });
    toast.success(
      `@${creator.username} ${tier.toUpperCase()}: ${okCount}/${files.length} subidos`
    );
    onUploaded && onUploaded();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter((f) =>
      f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    if (files.length) upload(files);
  };

  const colorClass =
    tier === "free"
      ? "from-blue-500/20 to-cyan-500/10 border-blue-500/30"
      : "from-amber-500/20 to-orange-500/10 border-amber-500/30";
  const tierLabel = tier === "free" ? "Free" : "VIP";
  const tierTextColor = tier === "free" ? "text-blue-200" : "text-amber-200";

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`bg-gradient-to-br ${colorClass} border rounded-lg p-3 transition-all ${
        dragOver ? "scale-[1.02] ring-2 ring-white/30" : ""
      }`}
      data-testid={`vault-tier-${tier}-${creator.username}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[11px] font-bold uppercase ${tierTextColor}`}>
          {tierLabel}
        </span>
        <div className="flex items-center gap-2 text-[10px] text-white/60">
          <span title="Media en biblioteca">
            <ImageIcon className="w-3 h-3 inline" /> {tierData.mediaCount}
          </span>
          <span title="Captions">
            <MessageSquare className="w-3 h-3 inline" /> {tierData.captionCount}
          </span>
          {tierData.enabled ? (
            <span className="text-green-400">●</span>
          ) : (
            <span className="text-white/30">●</span>
          )}
        </div>
      </div>
      <p className="text-[10px] text-white/40 truncate mb-2 font-mono">
        {tierData.channelId}
      </p>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={(e) => upload(Array.from(e.target.files || []))}
        className="hidden"
        data-testid={`vault-upload-input-${tier}-${creator.username}`}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        data-testid={`vault-upload-btn-${tier}-${creator.username}`}
        className="w-full py-2 bg-white/10 hover:bg-white/20 border border-white/10 text-white text-xs font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {uploading ? (
          <>
            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            {progress.done}/{progress.total}
          </>
        ) : (
          <>
            <Upload className="w-3.5 h-3.5" />
            Subir contenido
          </>
        )}
      </button>
      <p className="text-[9px] text-white/30 text-center mt-1">
        o arrastra fotos/videos aquí
      </p>
    </div>
  );
};

const CreatorCard = ({ creator, getAuthHeaders, onChange }) => {
  const [cleaning, setCleaning] = useState(false);

  const cleanup = async () => {
    if (
      !window.confirm(
        `¿Eliminar archivos huérfanos de @${creator.username}? (Borra registros sin archivo en disco)`
      )
    )
      return;
    setCleaning(true);
    try {
      const r = await fetch(
        `/api/admin/autopost/${creator.username}/media/cleanup-orphans`,
        { method: "POST", headers: { ...getAuthHeaders() } }
      );
      const d = await r.json();
      if (r.ok) {
        toast.success(
          `🧹 Eliminados ${d.deleted} huérfanos · ${d.kept} válidos`
        );
        onChange && onChange();
      } else {
        toast.error("Error al limpiar");
      }
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div
      className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all"
      data-testid={`vault-creator-${creator.username}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {creator.avatar ? (
            <img
              src={creator.avatar}
              alt=""
              className="w-10 h-10 rounded-full object-cover bg-white/10"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
              {creator.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-white font-semibold text-sm truncate">
              {creator.name}
            </h3>
            <p className="text-white/40 text-[11px] font-mono truncate">
              @{creator.username}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0">
          <button
            onClick={cleanup}
            disabled={cleaning}
            data-testid={`vault-cleanup-${creator.username}`}
            title="Limpiar archivos huérfanos"
            className="p-2 text-amber-300/70 hover:text-amber-300 rounded hover:bg-amber-500/10 disabled:opacity-50"
          >
            {cleaning ? (
              <div className="w-4 h-4 border-2 border-amber-300/30 border-t-amber-300 rounded-full animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
          <Link
            to={`/admin/creadoras/${creator.username}/auto-posting`}
            className="p-2 text-white/40 hover:text-white rounded hover:bg-white/10"
            title="Configuración avanzada"
          >
            <Settings className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Recent thumbnails */}
      {creator.recentThumbs?.length > 0 && (
        <div className="grid grid-cols-4 gap-1 mb-3">
          {creator.recentThumbs.map((t, i) => (
            <div
              key={i}
              className="aspect-square rounded overflow-hidden bg-black/40 relative"
            >
              {t.mediaType === "video" ? (
                <video src={t.mediaUrl} className="w-full h-full object-cover" muted />
              ) : (
                <img src={t.mediaUrl} alt="" className="w-full h-full object-cover" />
              )}
              {t.mediaType === "video" && (
                <Video className="w-3 h-3 absolute top-1 right-1 text-white/80" />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <TierUpload
          creator={creator}
          tier="free"
          getAuthHeaders={getAuthHeaders}
          onUploaded={onChange}
        />
        <TierUpload
          creator={creator}
          tier="vip"
          getAuthHeaders={getAuthHeaders}
          onUploaded={onChange}
        />
      </div>
    </div>
  );
};

const AdminBoveda = () => {
  const { getAuthHeaders } = useAuth();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [autoAssigning, setAutoAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/autopost/vault/creators`, {
        headers: { ...getAuthHeaders() },
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        if (r.status === 404) {
          toast.error(
            "Bóveda no disponible en producción todavía. Haz Deploy en Emergent.",
            { duration: 8000 }
          );
        } else {
          toast.error(`Respuesta inesperada (HTTP ${r.status})`);
        }
        setCreators([]);
        return;
      }
      const d = await r.json();
      setCreators(d.creators || []);
    } catch (e) {
      toast.error("Error al cargar la bóveda");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const autoAssignCaptions = async (overwrite) => {
    if (creators.length === 0) {
      toast.error("No hay modelos en la bóveda");
      return;
    }
    if (overwrite) {
      if (
        !window.confirm(
          "Esto REEMPLAZARÁ los captions actuales de todos los media items con uno aleatorio del pool. ¿Continuar?"
        )
      )
        return;
    }
    setAutoAssigning(true);
    let totalAssigned = 0;
    let totalSkipped = 0;
    let totalScanned = 0;
    try {
      for (const c of creators) {
        const r = await fetch(
          `/api/admin/autopost/${c.username}/media/auto-assign-captions?overwrite=${overwrite ? "true" : "false"}&useAI=true`,
          { method: "POST", headers: { ...getAuthHeaders() } }
        );
        if (r.ok) {
          const d = await r.json();
          totalAssigned += d.assigned || 0;
          totalSkipped += d.skipped || 0;
          totalScanned += d.scanned || 0;
        }
      }
      toast.success(
        `✨ ${totalAssigned} captions asignados (${totalScanned} escaneados, ${totalSkipped} sin pool)`,
        { description: "Se usó IA Gemini para los que tenían imagen", duration: 6000 }
      );
      load();
    } catch {
      toast.error("Error de red");
    } finally {
      setAutoAssigning(false);
    }
  };

  const filtered = creators.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.username.toLowerCase().includes(q) ||
      c.name.toLowerCase().includes(q)
    );
  });

  const totalMedia = creators.reduce(
    (acc, c) => acc + (c.free?.mediaCount || 0) + (c.vip?.mediaCount || 0),
    0
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-2">
              <Lock className="w-7 h-7 text-amber-400" />
              Bóveda
            </h1>
            <p className="text-white/60 text-sm">
              Carga contenido rápido a todas las modelos configuradas — sin
              navegar una por una
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => autoAssignCaptions(false)}
              disabled={autoAssigning}
              data-testid="vault-auto-assign-captions"
              title="Asignar caption del pool a los media que no tengan"
              className="inline-flex items-center gap-2 px-3 py-2 bg-pink-500/15 hover:bg-pink-500/25 border border-pink-500/30 text-pink-200 rounded-lg text-xs font-medium disabled:opacity-50"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {autoAssigning ? "Asignando…" : "Asignar captions"}
            </button>
            <button
              onClick={load}
              className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5"
              data-testid="vault-reload"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="text-white/60 text-xs">Modelos configuradas</div>
            <div className="text-white text-2xl font-bold">{creators.length}</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="text-white/60 text-xs">Total media</div>
            <div className="text-white text-2xl font-bold">{totalMedia}</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="text-white/60 text-xs">Promedio / modelo</div>
            <div className="text-white text-2xl font-bold">
              {creators.length > 0 ? Math.round(totalMedia / creators.length) : 0}
            </div>
          </div>
        </div>

        {/* Search */}
        {creators.length > 6 && (
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar modelo..."
              data-testid="vault-search"
              className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-white/30"
            />
          </div>
        )}

        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Lock className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-sm mb-2">
              {creators.length === 0
                ? "Aún no hay modelos con canales configurados"
                : "Sin resultados para tu búsqueda"}
            </p>
            {creators.length === 0 && (
              <Link
                to="/admin/auto-posting"
                className="inline-block mt-2 px-4 py-2 bg-white text-black text-xs font-bold rounded-lg"
              >
                Configurar canales →
              </Link>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((c) => (
              <CreatorCard
                key={c.username}
                creator={c}
                getAuthHeaders={getAuthHeaders}
                onChange={load}
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBoveda;
